import { faPhone, faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { faDownload, faExternalLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ComponentPropsWithoutRef, FC } from 'react';
import { useMemo } from 'react';
import type { LinkProps as RouterLinkProps } from 'react-router-dom';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import type { ActionMode } from 'src/components/action-mode';
import { useActionMode } from 'src/components/action-mode';
import {
  useActionGroupMode,
  type ActionGroupMode,
} from 'src/components/buttons-and-actions/action-group';
import {
  getActionGroupModeClasses,
  getBaseActionClasses,
  getDropdownItemClasses,
  getButtonInverseClasses,
  getButtonPrimaryClasses,
  getButtonSecondaryClasses,
  type Size,
  getTabClasses,
} from 'src/components/buttons-and-actions/button';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { cn } from 'src/utils/cn';
import type { StrictOmit } from 'src/utils/utility-types';

export type BaseLinkProps =
  | {
      mode?:
        | 'default'
        | 'inverse' // maybe we can delete this when we delete the legacy styles (only used in the footer)
        | 'button-inverse'; // maybe we can delete this when we delete the legacy styles (only used in menu)
      size?: undefined;
      /** This mode doesn't use `isActive`, but you can pass it, in case mode is switched dynamically. */
      isActive?: boolean;
      hideIcon?: undefined;
      /** This mode doesn't use `disabled`, but you can pass it, in case mode is switched dynamically. */
      disabled?: boolean;
    }
  | {
      mode?: 'default-underlined';
      size?: undefined;
      isActive?: undefined;
      /**
       * This is only relevant for external links. *Very* carefully use this.
       * E.g. if we point to an external site belonging to Prisma. This icon is meant
       * to be an indicator that the user is redirected to a 3rd party site.
       */
      hideIcon?: boolean;
      disabled?: undefined;
    }
  | {
      mode:
        | 'primary' // maybe we can delete this when we delete the legacy styles (only used in the header)
        | 'secondary' // maybe we can delete this when we delete the legacy styles (only used in the header)
        | 'tab';
      size?: undefined;
      isActive?: boolean;
      hideIcon?: undefined;
      disabled?: undefined;
    }
  | {
      mode: 'sidebar';
      size?: undefined;
      isActive?: boolean;
      hideIcon?: undefined;
      disabled?: boolean; // TODO: check if still needed (this was never really used for disabling the link)
    }
  | {
      mode: 'button-primary';
      size?: Size;
      /** This mode doesn't use `isActive`, but you can pass it, in case mode is switched dynamically. */
      isActive?: boolean;
      hideIcon?: undefined;
      disabled?: undefined;
    }
  | {
      mode: 'button-secondary';
      size?: Size;
      isActive?: boolean;
      hideIcon?: undefined;
      disabled?: undefined;
    }
  | {
      mode: 'quick-filter';
      size?: undefined;
      isActive?: boolean;
      hideIcon?: undefined;
      disabled?: undefined;
    }
  | {
      mode: 'dropdown-item';
      size?: undefined;
      isActive?: boolean;
      hideIcon?: undefined;
      disabled?: boolean;
    };

const routeAwareActiveness: BaseLinkProps['mode'][] = [
  'primary',
  'secondary',
  'tab',
  'dropdown-item',
  'sidebar',
];

export type LinkProps<S> = BaseLinkProps &
  StrictOmit<RouterLinkProps, 'state'> & { state?: S };

export const Link: FC<LinkProps<any>> = (baseProps) => {
  const actionMode = useActionMode();
  const { pathname } = useLocation();
  const actionGroupMode = useActionGroupMode();
  const isNewDesignActive = useIsNewDesignActive();

  // descruct all custom props
  const {
    mode,
    size,
    isActive: explicitIsActive,
    hideIcon,
    disabled,
    ...props
  } = {
    ...mapActionModeToLink(actionMode),
    ...baseProps,
  } as LinkProps<any>;

  // can be overwritten by passing `isActive` explicitly
  const isActive = useMemo(() => {
    if (explicitIsActive !== undefined) return explicitIsActive;
    if (!routeAwareActiveness.includes(mode)) return false;

    if (props.to) {
      const to =
        typeof props.to === 'string'
          ? props.to
          : typeof props.to === 'function'
            ? '' // note: function version is not supported
            : props.to.pathname || '';
      return pathname.startsWith(
        to.replace(`${PRISMA_CONFIG.angularUrl}/#`, '')
      );
    } else {
      return false;
    }
  }, [pathname, props.to, explicitIsActive, mode]);

  switch (mode) {
    case 'primary':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={cn(
            getBaseActionClasses({ isActive }),
            `
              border-b-2 border-b-transparent text-[1.6rem] text-white
              no-underline
              hover:border-b-white hover:text-white
              focus:border-b-white focus:text-white
            `,
            isActive && 'border-b-white text-white',
            props.className
          )}
        />
      );
    case 'secondary':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={cn(
            getBaseActionClasses({ isActive }),
            `
              border-b-2 border-b-transparent text-[1.6rem] text-legacy-brand
              no-underline
              hover:border-b-legacy-brand hover:text-legacy-brand
              focus:border-b-legacy-brand focus:text-legacy-brand
            `,
            isActive && 'border-b-legacy-brand text-legacy-brand',
            props.className
          )}
        />
      );
    case 'tab':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getTabClasses({
            isActive,
            className: props.className,
          })}
        />
      );
    case 'inverse':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={cn(
            getBaseActionClasses({ isActive }),
            `
              text-white no-underline
              not-legacy:text-label-base
              hover:text-legacy-brand-secondary
              focus:text-legacy-brand-secondary
              legacy:border-b-2 legacy:border-b-transparent
            `,
            props.className
          )}
        />
      );
    case 'dropdown-item':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getDropdownItemClasses({
            isActive,
            className: cn(
              `
                legacy:border-b-2 legacy:border-b-transparent
                legacy:leading-[1.42857143]
              `,
              props.className
            ),
          })}
        >
          {isNewDesignActive ? props.children : <span>{props.children}</span>}
        </CustomRouterLink>
      );
    case 'button-primary':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getButtonPrimaryClasses({
            actionGroupMode,
            size,
            className: props.className,
          })}
        />
      );
    case 'button-secondary':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getButtonSecondaryClasses({
            isActive,
            actionGroupMode,
            size,
            className: props.className,
          })}
        />
      );
    case 'button-inverse':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getButtonInverseClasses({
            actionGroupMode,
            className: props.className,
          })}
        />
      );
    case 'sidebar':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getSidebarItemClasses({
            isActive,
            className: props.className,
          })}
        >
          <span>{props.children}</span>
        </CustomRouterLink>
      );
    case 'quick-filter':
      return (
        <CustomRouterLink
          disabled={isActive}
          {...props}
          className={getQuickFilterClasses({
            isActive,
            actionGroupMode,
            className: props.className,
            isNewDesignActive,
          })}
        />
      );

    case 'default-underlined':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getDefaultUnderlinedLinkStyles({
            className: props.className,
          })}
        />
      );
    case 'default':
    case undefined:
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getDefaultLinkStyles({
            className: props.className,
          })}
        />
      );
  }
};

const disabledLinkProps: ComponentPropsWithoutRef<'a'> & {
  'data-force-disabled': true;
} = {
  href: undefined,
  role: 'link',
  'aria-disabled': true,
  'data-force-disabled': true,
};

export type ExternalLinkProps = BaseLinkProps &
  ComponentPropsWithoutRef<'a'> & {
    /**
     * This is needed if we do NOT want to convert an internal link from
     * <ExternalLink/> to <Link/>, because we WANT the refresh.
     */
    forceRefresh?: boolean;
  };

export const ExternalLink: FC<ExternalLinkProps> = ({
  disabled,
  ...baseProps
}) => {
  const actionMode = useActionMode();
  const { pathname, hash } = useLocation();
  const actionGroupMode = useActionGroupMode();
  const isNewDesignActive = useIsNewDesignActive();

  // see https://web.dev/external-anchors-use-rel-noopener
  const unsafeCrossOriginDestination =
    baseProps.target === '_blank' && !baseProps.rel;

  // descruct all custom props
  const {
    mode,
    size,
    isActive: explicitIsActive,
    hideIcon,
    forceRefresh,
    ...props
  } = {
    ...mapActionModeToLink(actionMode),
    ...baseProps,
    ...(unsafeCrossOriginDestination && { rel: 'noopener noreferrer' }),
    ...(disabled && disabledLinkProps),
  } as ExternalLinkProps;

  // can be overwritten by passing `isActive` explicitly
  const isActive = useMemo(() => {
    if (explicitIsActive !== undefined) return explicitIsActive;
    if (!routeAwareActiveness.includes(mode)) return false;

    if (props.href) {
      const cleanedHref = props.href.replace(
        `${PRISMA_CONFIG.angularUrl}/#`,
        ''
      );
      return pathname === '/platform' || pathname === '/platform/'
        ? hash.startsWith(`#${cleanedHref}`)
        : pathname.startsWith(cleanedHref);
    } else {
      return false;
    }
  }, [pathname, hash, props.href, explicitIsActive, mode]);

  // links to the old frontend used to point to a standalone Angular application
  // in the past (and therefor we used external links), but since we started to
  // embed Angular pages into React we manually convert them to internal links
  if (props.href?.startsWith(PRISMA_CONFIG.angularUrl) && !forceRefresh) {
    const to = props.href.replace(PRISMA_CONFIG.angularUrl, '/platform');
    const { href, ...baseLinkProps } = props;
    const linkProps = {
      ...baseLinkProps,
      isActive,
      to,
      mode,
      size,
      disabled,
    } as LinkProps<any>;
    return <Link {...linkProps} />;
  }

  switch (mode) {
    case 'primary':
      return (
        <a
          {...props}
          className={cn(
            getBaseActionClasses({ isActive }),
            `
              border-b-2 border-b-transparent text-[1.6rem] text-white
              no-underline
              hover:border-b-white hover:text-white
              focus:border-b-white focus:text-white
            `,
            isActive && 'border-b-white text-white',
            props.className
          )}
        />
      );
    case 'secondary':
      return (
        <a
          {...props}
          className={cn(
            getBaseActionClasses({ isActive }),
            `
              border-b-2 border-b-transparent text-[1.6rem] text-legacy-brand
              no-underline
              hover:border-b-legacy-brand hover:text-legacy-brand
              focus:border-b-legacy-brand focus:text-legacy-brand
            `,
            isActive && 'border-b-legacy-brand text-legacy-brand',
            props.className
          )}
        />
      );
    case 'tab':
      return (
        <a
          {...props}
          className={getTabClasses({
            isActive,
            className: props.className,
          })}
        />
      );
    case 'inverse':
      return (
        <a
          {...props}
          className={cn(
            getBaseActionClasses({ isActive }),
            `
              text-white no-underline
              not-legacy:text-label-base
              hover:text-legacy-brand-secondary
              focus:text-legacy-brand-secondary
              legacy:border-b-2 legacy:border-b-transparent
            `,
            props.className
          )}
        />
      );
    case 'dropdown-item':
      return (
        <a
          {...props}
          className={getDropdownItemClasses({
            isActive,
            className: cn(
              `
                legacy:border-b-2 legacy:border-b-transparent
                legacy:leading-[1.42857143]
              `,
              props.className
            ),
          })}
        >
          {isNewDesignActive ? props.children : <span>{props.children}</span>}
        </a>
      );
    case 'button-primary':
      return (
        <a
          {...props}
          className={getButtonPrimaryClasses({
            actionGroupMode,
            size,
            className: props.className,
          })}
        />
      );
    case 'button-secondary':
      return (
        <a
          {...props}
          className={getButtonSecondaryClasses({
            isActive,
            actionGroupMode,
            size,
            className: props.className,
          })}
        />
      );
    case 'button-inverse':
      return (
        <a
          {...props}
          className={getButtonInverseClasses({
            actionGroupMode,
            className: props.className,
          })}
        />
      );
    case 'sidebar':
      return (
        <a
          {...props}
          className={getSidebarItemClasses({
            isActive,
            className: props.className,
          })}
        >
          <span>{props.children}</span>
        </a>
      );
    case 'quick-filter':
      return (
        <a
          {...props}
          {...(isActive && disabledLinkProps)}
          className={getQuickFilterClasses({
            isActive,
            actionGroupMode,
            className: props.className,
            isNewDesignActive,
          })}
        />
      );
    case 'default-underlined':
      return (
        <span>
          <a
            {...props}
            className={getDefaultUnderlinedLinkStyles({
              className: props.className,
            })}
          />
          {!hideIcon && (
            <>
              {' '}
              <FontAwesomeIcon
                className="not-legacy:text-foreground-primary-inverted"
                icon={
                  props.download || isFileUri(props.href)
                    ? faDownload
                    : props.href?.startsWith('tel:')
                      ? faPhone
                      : props.href?.startsWith('mailto:')
                        ? faEnvelope
                        : faExternalLink
                }
              />
            </>
          )}
        </span>
      );
    case 'default':
    case undefined:
      return (
        <a
          {...props}
          className={getDefaultLinkStyles({
            className: props.className,
          })}
        />
      );
  }
};

function getSidebarItemClasses({
  isActive,
  className,
}: {
  isActive?: boolean;
  className?: string;
}) {
  return cn(
    getBaseActionClasses({ isActive }),
    `
      block border-b-2 border-b-transparent px-[1.5rem] py-[0.5rem]
      text-[1.7rem] text-legacy-brand no-underline
      hover:[&>span]:border-b-[0.2rem] hover:[&>span]:border-legacy-brand
      focus:[&>span]:border-b-[0.2rem] focus:[&>span]:border-legacy-brand
    `,
    isActive && `[&>span]:border-b-[0.2rem] [&>span]:border-legacy-brand`,
    className
  );
}

function getQuickFilterClasses({
  isActive,
  actionGroupMode,
  className,
  isNewDesignActive,
}: {
  isActive?: boolean;
  actionGroupMode: ActionGroupMode;
  className?: string;
  isNewDesignActive: boolean;
}) {
  // TODO: discuss if we can merge these styles in the future as there is no difference anymore
  if (isNewDesignActive)
    return getButtonSecondaryClasses({
      isActive,
      actionGroupMode,
      size: 'small',
      className,
    });

  return cn(
    getBaseActionClasses({ isActive }),
    getActionGroupModeClasses({ actionGroupMode }),
    `
      legacy:border-[0.1rem] legacy:border-legacy-brand-light-3
      legacy:bg-legacy-brand-light-4 legacy:px-[2rem] legacy:py-[0.6rem]
      legacy:text-legacy-brand
    `,
    isActive
      ? `legacy:shadow-[inset_0.1rem_0.1rem_0.4rem_rgba(0,0,0,0.25)]`
      : `
        legacy:hover:border-legacy-brand-secondary
        legacy:hover:bg-legacy-brand-secondary legacy:hover:text-white
        legacy:focus:border-legacy-brand-secondary
        legacy:focus:bg-legacy-brand-secondary legacy:focus:text-white
      `,
    className
  );
}

export function getDefaultLinkStyles({ className }: { className?: string }) {
  return cn(
    getBaseActionClasses({ isActive: undefined }),
    `
      text-foreground-primary-inverted no-underline
      not-legacy:text-label-base
      not-active:hover:text-foreground-secondary-hover
      active:text-foreground-secondary-active
    `,
    `
      legacy:border-b-2 legacy:border-b-transparent legacy:text-legacy-brand
      legacy:not-active:hover:text-legacy-brand-secondary
      legacy:focus:text-legacy-brand-secondary
    `,
    className
  );
}

export function getDefaultUnderlinedLinkStyles({
  className,
}: {
  className?: string;
}) {
  return getDefaultLinkStyles({
    className: cn(
      `
        legacy:border-b-0 legacy:underline
        legacy:decoration-legacy-brand-light-2
      `,
      className
    ),
  });
}

// allow "disabled" state
const CustomRouterLink: FC<
  { disabled?: boolean } & RouterLinkProps &
    StrictOmit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>
> = ({
  // custom props
  disabled,
  // router link props
  reloadDocument,
  replace,
  state,
  preventScrollReset,
  relative,
  to,
  viewTransition,
  // native props
  ...rest
}) => {
  if (disabled) {
    return <a {...rest} {...disabledLinkProps} />;
  }

  return (
    <RouterLink
      {...rest}
      reloadDocument={reloadDocument}
      replace={replace}
      state={state}
      preventScrollReset={preventScrollReset}
      relative={relative}
      to={to}
      viewTransition={viewTransition}
    />
  );
};

function isFileUri(uri?: string) {
  if (!uri) return false;
  const url = new URL(uri);
  return Boolean(/\/[^/]+\.[^/]+$/.test(url.pathname));
}

function mapActionModeToLink(
  actionMode: ActionMode | null
): BaseLinkProps | null {
  if (!actionMode) return null;

  switch (actionMode.mode) {
    case 'button-primary':
      return { ...actionMode, mode: 'button-primary' };
    case 'button-secondary':
      return { ...actionMode, mode: 'button-secondary' };
    case 'link-primary':
      return { ...actionMode, mode: 'primary' };
    case 'dropdown':
      return { ...actionMode, mode: 'dropdown-item' };
    case 'quick-filter':
      return { ...actionMode, mode: 'quick-filter' };
    case 'sidebar':
      return { ...actionMode, mode: 'sidebar' };
    case 'tab':
      return { ...actionMode, mode: 'tab' };
  }
}
